/**
 * @voltxampere/presets — Three-resistor divider (Sprint 50-2)
 *
 * Multi-tap voltage divider with three series resistors. With
 * V1=12V and R1=R2=R3=1kΩ, the two intermediate node voltages are
 * V_a = 8V (after R1) and V_b = 4V (after R2). Used in op-amp
 * reference circuits, ADC ladders, and resistor-string DACs.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { makeCircuit, makeNet, makeComponent } from "../_builders.js";
export const threeResistorDivider = {
    id: "three-resistor-divider",
    name: { tr: "Üç Dirençli Bölücü", en: "Three-Resistor Divider" },
    category: "basic",
    difficulty: 2,
    description: {
        tr: "Üç eşit direnç ile çoklu çıkış. V1=12V → V_a=8V, V_b=4V.",
        en: "Three equal resistors create multiple taps. V1=12V → V_a=8V, V_b=4V.",
    },
    circuit: makeCircuit({
        slot: 1,
        name: "Three-Resistor Divider",
        description: "Multi-tap voltage divider",
        nets: [
            makeNet({ name: "gnd", isGround: true }),
            makeNet({ name: "vcc" }),
            makeNet({ name: "a" }),
            makeNet({ name: "b" }),
        ],
        components: [
            makeComponent({
                id: "V1",
                type: "voltage-source",
                params: { voltage: 12 },
                terminals: { pos: "net_vcc", neg: "net_gnd" },
                label: "V1",
                position: { x: -160, y: 0 },
                rotation: 0,
            }),
            makeComponent({
                id: "R1",
                type: "resistor",
                params: { resistance: 1000 },
                terminals: { p1: "net_vcc", p2: "net_a" },
                label: "R1",
                position: { x: 0, y: -80 },
                rotation: 90,
            }),
            makeComponent({
                id: "R2",
                type: "resistor",
                params: { resistance: 1000 },
                terminals: { p1: "net_a", p2: "net_b" },
                label: "R2",
                position: { x: 0, y: 0 },
                rotation: 90,
            }),
            makeComponent({
                id: "R3",
                type: "resistor",
                params: { resistance: 1000 },
                terminals: { p1: "net_b", p2: "net_gnd" },
                label: "R3",
                position: { x: 0, y: 80 },
                rotation: 90,
            }),
            makeComponent({
                id: "GND1",
                type: "ground",
                params: {},
                terminals: { p: "net_gnd" },
                position: { x: -160, y: 80 },
                rotation: 0,
            }),
        ],
        wires: [
            {
                id: "w_vcc",
                netId: "net_vcc",
                path: [
                    { x: -160, y: -10 },
                    { x: -160, y: -100 },
                    { x: 0, y: -100 },
                ],
                style: "orthogonal",
            },
            {
                id: "w_a",
                netId: "net_a",
                path: [
                    { x: 0, y: -60 },
                    { x: 0, y: -20 },
                ],
                style: "orthogonal",
            },
            {
                id: "w_b",
                netId: "net_b",
                path: [
                    { x: 0, y: 20 },
                    { x: 0, y: 60 },
                ],
                style: "orthogonal",
            },
            {
                id: "w_gnd",
                netId: "net_gnd",
                path: [
                    { x: -160, y: 10 },
                    { x: -160, y: 71 },
                    { x: 0, y: 71 },
                    { x: 0, y: 100 },
                ],
                style: "orthogonal",
            },
        ],
    }),
    expectedValues: [
        {
            node: "a",
            analysis: "dc",
            value: 8,
            tolerance: 0.001,
            unit: "V",
        },
        {
            node: "b",
            analysis: "dc",
            value: 4,
            tolerance: 0.001,
            unit: "V",
        },
    ],
    learningGoals: [
        {
            tr: "Eşit dirençlerle simetrik bölme: V_n = V1·(N-n)/N.",
            en: "Symmetric division with equal resistors: V_n = V1·(N-n)/N.",
        },
        {
            tr: "Çoklu çıkış noktasıyla referans ladder oluşumu.",
            en: "Reference ladder construction with multiple tap points.",
        },
    ],
    references: ["Sedra/Smith, Microelectronic Circuits, Ch. 1"],
};
//# sourceMappingURL=three-resistor-divider.js.map